import { useEffect, useRef } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { useParcels, useVariants } from "../hooks";
import type { RouteVersion } from "../types";
import { parcelFillColor } from "./ui";

function currentVersionOf(
  variants: { versions: RouteVersion[]; is_binding: boolean }[] | undefined,
): RouteVersion | null {
  // Prefer the binding variant — its current version is the authoritative route.
  const ordered = [...(variants ?? [])].sort(
    (a, b) => Number(b.is_binding) - Number(a.is_binding),
  );
  for (const v of ordered) {
    const cur = v.versions.find((ver) => ver.is_current) || v.versions[0];
    if (cur) return cur;
  }
  return null;
}

/** Compact read-only map for the parcel drawer: our route (red) + demolition lines
 *  (blue) + parcels coloured by status, CENTRED on this parcel (which is highlighted).
 *  Reuses the same data + styling as the full MapTab, without the editing chrome. */
export function ParcelRouteMiniMap({
  connectionId,
  parcelId,
  height = 340,
}: {
  connectionId: string;
  parcelId: string;
  /** Wysokość mapy w px — w szufladzie działki („Przebieg trasy") jest miejsce na więcej. */
  height?: number;
}) {
  const { data: variants } = useVariants(connectionId);
  const { data: parcels } = useParcels(connectionId);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const overlayRef = useRef<L.LayerGroup | null>(null);

  useEffect(() => {
    if (mapRef.current || !containerRef.current) return;
    const map = L.map(containerRef.current, {
      center: [52.0, 19.0],
      zoom: 6,
      attributionControl: false,
    });
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 21,
    }).addTo(map);
    overlayRef.current = L.layerGroup().addTo(map);
    mapRef.current = map;
    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    const group = overlayRef.current;
    if (!map || !group) return;
    group.clearLayers();

    // Parcels coloured as on the main map (status colour; notification = light green;
    // spare „na zapas" = sand — see parcelFillColor). The FOCUS parcel is drawn last,
    // darker + bolder.
    let focusGeom: unknown = null;
    for (const p of parcels ?? []) {
      if (!p.geometry) continue;
      const isFocus = p.id === parcelId;
      if (!isFocus && !p.in_current_route && !p.is_manual) continue;
      if (isFocus) focusGeom = p.geometry;
      const fillColor = parcelFillColor(p);
      L.geoJSON(p.geometry as never, {
        style: {
          color: isFocus ? "#111827" : "#555",
          weight: isFocus ? 2.5 : 1,
          fillColor,
          fillOpacity: isFocus ? 0.55 : 0.3,
        },
      }).addTo(group);
    }

    // Our route (red) + demolition lines (blue) on top.
    const cur = currentVersionOf(variants);
    if (cur?.geometry)
      L.geoJSON(cur.geometry as never, {
        style: { color: "#dc1f1f", weight: 3 },
      }).addTo(group);
    if (cur?.demolition_geometry)
      L.geoJSON(cur.demolition_geometry as never, {
        style: { color: "#0ea5e9", weight: 4 },
      }).addTo(group);

    // Centre on THIS parcel; fall back to the route footprint if it has no geometry.
    const raf = requestAnimationFrame(() => {
      map.invalidateSize();
      const target = focusGeom ?? cur?.geometry ?? null;
      if (!target) return;
      const b = L.geoJSON(target as never).getBounds();
      if (b.isValid())
        map.fitBounds(b, {
          padding: focusGeom ? [70, 70] : [30, 30],
          maxZoom: focusGeom ? 18 : 19,
        });
    });
    return () => cancelAnimationFrame(raf);
  }, [parcels, variants, parcelId]);

  return (
    <div className="relative isolate z-0">
      <div
        ref={containerRef}
        className="w-full border border-[var(--color-border)]"
        style={{ height, background: "#fff" }}
      />
      {/* Tiny legend so the colours read without the full map's controls. */}
      <div className="absolute bottom-2 left-2 z-[1000] card bg-[var(--color-bg-surface)] shadow-sm px-2 py-1 font-mono text-[10px] text-[var(--color-text-secondary)] space-y-0.5">
        <div className="flex items-center gap-1.5">
          <span className="inline-block w-3 h-0.5" style={{ background: "#dc1f1f" }} />
          trasa
        </div>
        <div className="flex items-center gap-1.5">
          <span className="inline-block w-3 h-0.5" style={{ background: "#0ea5e9" }} />
          demontaż
        </div>
      </div>
    </div>
  );
}
